import { logError } from "@/lib/error-handling";
import { apiClient } from "../api";
import type { HubSpotUserSyncScope } from "@/types/hubspot-user-sync";
import type { HubSpotCompanySyncScope } from "@/types/hubspot-company-sync";
import type { UserCreationMode } from "@/types/salesforce-user-sync";
import type { InstallationStatus } from "@/lib/hubspot/installation-status";

export interface SaveHubSpotUserSyncConfigParams {
  scope: HubSpotUserSyncScope;
  creation_mode?: UserCreationMode;
}

export interface SaveHubSpotCompanySyncConfigParams {
  scope: HubSpotCompanySyncScope;
}

export interface SaveHubSpotFieldMappingsParams {
  mappings: Array<{
    providerField: string;
    skriftField: string;
    category?: "required" | "recommended" | "additional";
  }>;
}

export async function saveHubSpotUserSyncConfig(
  params: SaveHubSpotUserSyncConfigParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-user-sync-config",
      {
        method: "POST",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "saveHubSpotUserSyncConfig");
    throw error;
  }
}

export async function updateHubSpotUserSyncConfig(
  params: SaveHubSpotUserSyncConfigParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-user-sync-config",
      {
        method: "PATCH",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "updateHubSpotUserSyncConfig");
    throw error;
  }
}

export async function saveHubSpotCompanySyncConfig(
  params: SaveHubSpotCompanySyncConfigParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-company-sync-config",
      {
        method: "POST",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "saveHubSpotCompanySyncConfig");
    throw error;
  }
}

export async function updateHubSpotCompanySyncConfig(
  params: SaveHubSpotCompanySyncConfigParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-company-sync-config",
      {
        method: "PATCH",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "updateHubSpotCompanySyncConfig");
    throw error;
  }
}

export async function saveHubSpotFieldMappings(
  params: SaveHubSpotFieldMappingsParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-field-mappings",
      {
        method: "POST",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "saveHubSpotFieldMappings");
    throw error;
  }
}

export async function updateHubSpotFieldMappings(
  params: SaveHubSpotFieldMappingsParams
) {
  try {
    return await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/save-field-mappings",
      {
        method: "PATCH",
        body: JSON.stringify(params),
      }
    );
  } catch (error) {
    logError(error, "updateHubSpotFieldMappings");
    throw error;
  }
}

export async function fetchHubSpotInstallationStatus(): Promise<InstallationStatus> {
  try {
    return await apiClient<InstallationStatus>(
      "/api/hubspot/installation-status"
    );
  } catch (error) {
    logError(error, "fetchHubSpotInstallationStatus");
    throw error;
  }
}

export async function disconnectHubSpot(mode: "soft" | "hard"): Promise<void> {
  try {
    await apiClient<{ success: boolean; message: string }>(
      "/api/hubspot/disconnect",
      {
        method: "POST",
        body: JSON.stringify({ mode }),
      }
    );
  } catch (error) {
    logError(error, "disconnectHubSpot");
    throw error;
  }
}
